import type { Brand } from "@borealis/layout-express";
import { Trans, useLingui } from "@lingui/react";
import type { CarAd } from "@mobility-item-page/types";
import { formatNumber } from "@mobility-item-page/utilities";
import { Button } from "@warp-ds/elements/react/button";
import { Slider, SliderThumb } from "@warp-ds/elements/react/slider";
import { Toggle } from "@warp-ds/react";
import { type ComponentRef, useCallback, useEffect, useId, useRef, useState } from "react";
import { usesScandinavianMiles } from "src/utils/brand.ts";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";
import { useAppConfig } from "../../../context/AppConfigProviderContext.tsx";
import { Icon } from "../Icon.tsx";
import { Tooltip } from "../Tooltip.tsx";
import { EvInfoCard } from "./EvInfoCard.tsx";
import { evSavingsCalculatorUsed } from "./evTrackingEvents.ts";

type Props = {
  ad: CarAd;
};

type ChargingCosts = NonNullable<CarAd["charging_costs"]>;

/** Branch key for the ICU `select`s below. A boolean would reach `<Trans>` as a component placeholder. */
type UsesMiles = "true" | "false";

/** The car the electric one is costed against. Doubles as a `select` branch key and a state key. */
type FuelType = "petrol" | "diesel";

/** What the pump costs is a national matter, so each brand starts from its own country's prices. */
const FUEL_PRICE_DEFAULTS: Record<string, Record<FuelType, number>> = {
  finn: { petrol: 20, diesel: 20 },
  blocket: { petrol: 17, diesel: 20 },
  dba: { petrol: 18, diesel: 20 },
};

/** Fixed, not taken from the ad's `petrol_comparison`: the two fuels need a starting point each. */
const FUEL_CONSUMPTION_DEFAULTS = {
  petrol: { scandinavianMile: 0.65, kilometer: 6.5 },
  diesel: { scandinavianMile: 0.5, kilometer: 5 },
} satisfies Record<FuelType, { scandinavianMile: number; kilometer: number }>;

type Bounds = { min: number; max: number; step: number; decimals?: number };

/** In the unit each slider is shown in. */
const SLIDER_BOUNDS = {
  annualDistance: {
    scandinavianMile: { min: 100, max: 5_000, step: 100 },
    kilometer: { min: 1_000, max: 50_000, step: 1_000 },
  },
  electricityPrice: { min: 5, max: 800, step: 5 },
  fuelPrice: {
    finn: { min: 5, max: 35, step: 0.1, decimals: 1 },
    blocket: { min: 5, max: 25, step: 0.1, decimals: 2 },
    other: { min: 5, max: 25, step: 0.1, decimals: 1 },
  },
  fuelConsumption: {
    scandinavianMile: { min: 0.3, max: 2, step: 0.01, decimals: 2 },
    kilometer: { min: 3, max: 20, step: 0.1, decimals: 1 },
  },
} satisfies Record<string, Bounds | Record<string, Bounds>>;

const fuelPriceBoundsFor = (brand: Brand): Bounds => {
  switch (brand.toLowerCase()) {
    case "finn":
      return SLIDER_BOUNDS.fuelPrice.finn;
    case "blocket":
      return SLIDER_BOUNDS.fuelPrice.blocket;
    default:
      return SLIDER_BOUNDS.fuelPrice.other;
  }
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** Puts an ad figure on the slider's own scale, so the thumb can be moved back to where it started. */
const snapToSlider = (value: number, { min, max, step, decimals = 0 }: Bounds) => {
  const stepped = min + Math.round((clamp(value, min, max) - min) / step) * step;
  // Without the rounding, a 0.01 step turns 0.56 into 0.5600000000000001.
  return clamp(Number(stepped.toFixed(decimals)), min, max);
};

/** Savings calculator card — one of the cards inside the EV info section. */
export const SavingsCard = ({ ad }: Props) => {
  const { i18n } = useLingui();

  const chargingCosts = ad.charging_costs;

  if (chargingCosts?.source !== "AD_SPECIFIC") {
    return null;
  }

  return (
    <EvInfoCard
      testId="ev-savings"
      title={i18n.t({
        id: "itemPageLayout.ev.savings.title",
        message: "Estimated savings",
        comment: "Heading of the savings calculator card on electric car ads",
      })}
    >
      <SavingsCalculator chargingCosts={chargingCosts} />
    </EvInfoCard>
  );
};

const SavingsCalculator = ({ chargingCosts }: { chargingCosts: ChargingCosts }) => {
  const { i18n } = useLingui();
  const { locale, brand } = useAppConfig();

  const inMiles = usesScandinavianMiles(brand);
  const usesMiles: UsesMiles = inMiles ? "true" : "false";
  const distanceBounds = inMiles
    ? SLIDER_BOUNDS.annualDistance.scandinavianMile
    : SLIDER_BOUNDS.annualDistance.kilometer;
  const consumptionBounds = inMiles
    ? SLIDER_BOUNDS.fuelConsumption.scandinavianMile
    : SLIDER_BOUNDS.fuelConsumption.kilometer;
  const fuelPriceBounds = fuelPriceBoundsFor(brand);

  const { monthly_savings: savings, home_charging: homeCharging } = chargingCosts;

  const [annualDistance, setAnnualDistance] = useState(() =>
    snapToSlider(inMiles ? savings.annual_scandinavian_mile : savings.annual_km, distanceBounds),
  );
  // In øre, which is what the slider shows; the ad quotes kroner.
  const [electricityPrice, setElectricityPrice] = useState(() =>
    snapToSlider(homeCharging.price_pr_kwh * 100, SLIDER_BOUNDS.electricityPrice),
  );

  const [fuelType, setFuelType] = useState<FuelType>("petrol");
  // Kept per fuel type so switching back returns the reader to the figures they set.
  const [fuelPrices, setFuelPrices] = useState<Record<FuelType, number>>(() => {
    const prices = FUEL_PRICE_DEFAULTS[brand.toLowerCase()] ?? FUEL_PRICE_DEFAULTS.finn;
    return {
      petrol: snapToSlider(prices.petrol, fuelPriceBounds),
      diesel: snapToSlider(prices.diesel, fuelPriceBounds),
    };
  });
  // Litres per mil where distance is quoted in mil, litres per 100 km elsewhere.
  const [fuelConsumptions, setFuelConsumptions] = useState<Record<FuelType, number>>(() => {
    const unit = inMiles ? "scandinavianMile" : "kilometer";
    return {
      petrol: snapToSlider(FUEL_CONSUMPTION_DEFAULTS.petrol[unit], consumptionBounds),
      diesel: snapToSlider(FUEL_CONSUMPTION_DEFAULTS.diesel[unit], consumptionBounds),
    };
  });

  const fuelPrice = fuelPrices[fuelType];
  const fuelConsumption = fuelConsumptions[fuelType];

  const [showsCalculation, setShowsCalculation] = useState(false);
  const calculationId = useId();

  const { trackClick } = usePulse();
  // One event per page load: MO_ev108 counts the readers who put the calculator to use, not the
  // drags they make. Either the mileage slider or opening the prices below it is that first use.
  const hasTrackedEngagement = useRef(false);

  const trackEngagement = () => {
    if (hasTrackedEngagement.current) {
      return;
    }
    hasTrackedEngagement.current = true;
    // Tracking event: MO_ev108
    trackClick(evSavingsCalculatorUsed());
  };

  const monthlyDistance = annualDistance / 12;

  // Out of `range_pr_kwh`, not the `kwh_pr_scandinavian_mile` beside it: that is rounded to two
  // decimals, which costs a krone a month and disagrees with the server.
  const kwhPerDistance = (inMiles ? 10 : 1) / chargingCosts.range_pr_kwh;
  // The km slider reads per 100 km.
  const litresPerDistance = inMiles ? fuelConsumption : fuelConsumption / 100;

  // Not the ad's own `cost_pr_km`: that is fixed to the brand's electricity price, not the slider's.
  const monthlyChargingCost = monthlyDistance * kwhPerDistance * (electricityPrice / 100);
  const monthlyFuelCost = monthlyDistance * litresPerDistance * fuelPrice;
  const monthlySaving = monthlyFuelCost - monthlyChargingCost;

  const formatAmount = (amount: number) => formatNumber(amount, locale, { maximumFractionDigits: 0 }) ?? "";

  // Not `<Trans>`: the tooltip beside it needs the same text as a string, to name the panel it opens.
  const savingTitle = i18n.t({
    id: "itemPageLayout.ev.savings.monthlySaving.title",
    message: "{fuelType, select, diesel {Savings compared to a diesel car} other {Savings compared to a petrol car}}",
    comment:
      "Label above the monthly saving on the savings calculator card on electric car ads. 'fuelType' is the car the reader has picked to compare against.",
    values: { fuelType },
  });

  const fuelTypeOptions: { label: string; value: FuelType }[] = [
    {
      label: i18n.t({
        id: "itemPageLayout.ev.savings.fuelType.petrol",
        message: "Petrol",
        comment:
          "One of the two cars the savings calculator card on electric car ads can compare against. Bensin in the Nordic languages.",
      }),
      value: "petrol",
    },
    {
      label: i18n.t({
        id: "itemPageLayout.ev.savings.fuelType.diesel",
        message: "Diesel",
        comment: "One of the two cars the savings calculator card on electric car ads can compare against",
      }),
      value: "diesel",
    },
  ];

  const consumptionTitle =
    fuelType === "diesel"
      ? i18n.t({
          id: "itemPageLayout.ev.savings.fuelConsumption.diesel.title",
          message: "Diesel car consumption",
          comment:
            "Label of the consumption slider on the savings calculator card on electric car ads, when comparing against a diesel car. The unit is shown with the value, so it is left out here.",
        })
      : i18n.t({
          id: "itemPageLayout.ev.savings.fuelConsumption.petrol.title",
          message: "Gasoline car consumption",
          comment:
            "Label of the consumption slider on the savings calculator card on electric car ads, when comparing against a petrol car. The unit is shown with the value, so it is left out here.",
        });

  return (
    <>
      {/* Grouped in `div`s so the two running costs can sit side by side under the full-width saving. */}
      <dl className="mt-0 mb-24 p-16 rounded-8 s-bg-info-subtle dark:s-bg-subtle grid grid-cols-2 gap-x-16 gap-y-16">
        <div className="col-span-2">
          {/* `mx-0` throughout: the Warp reset indents `dt` and `dd` by 16px. */}
          <dt className="mx-0 s-text-subtle">
            {/* Keeps the icon on the line of the words it explains. */}
            <span className="whitespace-nowrap">
              {savingTitle}
              <Tooltip
                tooltip={i18n.t({
                  id: "itemPageLayout.ev.savings.disclaimer",
                  message:
                    "Calculated using estimated home charging costs vs average fuel costs based on the car’s official consumption when new. Savings exclude charging box service fees, vehicle tax, insurance, servicing and will be lower if using public fast chargers.",
                  comment:
                    "What the info icon after the monthly saving on the savings calculator card on electric car ads explains: the assumptions the saving rests on",
                })}
                label={savingTitle}
              />
            </span>
          </dt>
          <dd className="t2 font-bold mx-0 mt-4 mb-0">
            {i18n.t({
              id: "itemPageLayout.ev.savings.monthlySaving.value",
              message: "{amount} kr/Mo.",
              comment:
                "The monthly saving on the savings calculator card on electric car ads. 'Mo.' is the abbreviation for month.",
              values: { amount: formatAmount(monthlySaving) },
            })}
          </dd>
        </div>
        <div>
          <dt className="mx-0 s-text-subtle">
            <Trans
              id="itemPageLayout.ev.savings.monthlyChargingCost.title"
              message="Charging cost"
              comment="Label above the monthly charging cost on the savings calculator card on electric car ads. The amount below carries the per-month unit."
            />
          </dt>
          <dd className="mx-0 mt-4 mb-0">
            {i18n.t({
              id: "itemPageLayout.ev.savings.monthlyChargingCost.value",
              message: "{amount} kr/Mo.",
              comment:
                "The monthly charging cost on the savings calculator card on electric car ads. 'Mo.' is the abbreviation for month.",
              values: { amount: formatAmount(monthlyChargingCost) },
            })}
          </dd>
        </div>
        <div>
          <dt className="mx-0 s-text-subtle">
            <Trans
              id="itemPageLayout.ev.savings.monthlyFuelCost.title"
              message="{fuelType, select, diesel {Diesel cost} other {Petrol cost}}"
              comment="Label above the monthly fuel cost on the savings calculator card on electric car ads. 'fuelType' is the car the reader has picked to compare against. The amount below carries the per-month unit."
              values={{ fuelType }}
            />
          </dt>
          <dd className="mx-0 mt-4 mb-0">
            {i18n.t({
              id: "itemPageLayout.ev.savings.monthlyFuelCost.value",
              message: "{amount} kr/Mo.",
              comment:
                "The monthly fuel cost on the savings calculator card on electric car ads. 'Mo.' is the abbreviation for month.",
              values: { amount: formatAmount(monthlyFuelCost) },
            })}
          </dd>
        </div>
      </dl>

      <CalculatorSlider
        name="annual-distance"
        label={i18n.t({
          id: "itemPageLayout.ev.savings.annualDistance.title",
          message: "Your annual mileage",
          comment: "Label of the annual mileage slider on the savings calculator card on electric car ads",
        })}
        unit={i18n.t({
          id: "itemPageLayout.ev.savings.annualDistance.unit",
          message: "{usesMiles, select, true {mil} other {km}}",
          comment:
            "Unit after the annual mileage on the savings calculator card on electric car ads. 'usesMiles' is true on the brands that quote distance in Scandinavian mil (FINN, Blocket) and false where it is in km (DBA).",
          values: { usesMiles },
        })}
        value={annualDistance}
        {...distanceBounds}
        onChange={(next) => {
          trackEngagement();
          setAnnualDistance(next);
        }}
      />

      {/* Not the `ExpandableSection` the other cards use: its fade would half-show a slider. */}
      <Button
        variant="link"
        className="mt-16"
        // A string, not a boolean: React writes `aria-expanded=""` on later updates to a custom element.
        aria-expanded={showsCalculation ? "true" : "false"}
        aria-controls={calculationId}
        onClick={() => {
          if (!showsCalculation) {
            trackEngagement();
          }
          setShowsCalculation((shown) => !shown);
        }}
      >
        <span className="flex items-center gap-8 font-bold text-s">
          <Icon name={showsCalculation ? "Minus" : "Plus"} />
          {showsCalculation ? (
            <Trans
              id="itemPageLayout.ev.savings.hideCalculation"
              message="Hide price calculation"
              comment="Closes the prices the savings calculator card on electric car ads works from"
            />
          ) : (
            <Trans
              id="itemPageLayout.ev.savings.showCalculation"
              message="Show price calculation"
              comment="Opens the prices the savings calculator card on electric car ads works from"
            />
          )}
        </span>
      </Button>

      <div id={calculationId} hidden={!showsCalculation} className="mt-16 flex flex-col gap-16">
        <CalculatorSlider
          name="electricity-price"
          label={i18n.t({
            id: "itemPageLayout.ev.savings.electricityPrice.title",
            message: "Your electricity price",
            comment: "Label of the electricity price slider on the savings calculator card on electric car ads",
          })}
          tooltip={i18n.t({
            id: "itemPageLayout.ev.savings.electricityPrice.tooltip",
            message:
              "Your price per kWh when charging at home (incl. grid fees and taxes), as shown on your electricity bill.",
            comment: "Explains the electricity price slider on the savings calculator card on electric car ads",
          })}
          unit={i18n.t({
            id: "itemPageLayout.ev.savings.electricityPrice.unit",
            message: "øre/kWh",
            comment:
              "Unit after the electricity price on the savings calculator card on electric car ads. A hundredth of the local currency per kilowatt hour.",
          })}
          value={electricityPrice}
          {...SLIDER_BOUNDS.electricityPrice}
          onChange={setElectricityPrice}
        />

        <Toggle
          type="radio-button"
          equalWidth
          title={i18n.t({
            id: "itemPageLayout.ev.savings.fuelType.title",
            message: "Compare against",
            comment:
              "Label of the petrol/diesel choice on the savings calculator card on electric car ads, which picks the car the electric one is costed against",
          })}
          options={fuelTypeOptions}
          selected={fuelTypeOptions.filter((option) => option.value === fuelType)}
          onChange={(option: { value: FuelType }) => setFuelType(option.value)}
        />

        <CalculatorSlider
          name="fuel-price"
          label={i18n.t({
            id: "itemPageLayout.ev.savings.fuelPrice.title",
            message: "{fuelType, select, diesel {Diesel price} other {Petrol price}}",
            comment:
              "Label of the fuel price slider on the savings calculator card on electric car ads. 'fuelType' is the car the reader has picked to compare against.",
            values: { fuelType },
          })}
          tooltip={i18n.t({
            id: "itemPageLayout.ev.savings.fuelPrice.tooltip",
            message: "Drag to match the price you pay per liter",
            comment: "Explains the fuel price slider on the savings calculator card on electric car ads",
          })}
          unit={i18n.t({
            id: "itemPageLayout.ev.savings.fuelPrice.unit",
            message: "kr/l",
            comment:
              "Unit after the fuel price on the savings calculator card on electric car ads. The local currency per litre.",
          })}
          value={fuelPrice}
          {...fuelPriceBounds}
          onChange={(next) => setFuelPrices((prices) => ({ ...prices, [fuelType]: next }))}
        />

        <CalculatorSlider
          name="fuel-consumption"
          label={consumptionTitle}
          unit={i18n.t({
            id: "itemPageLayout.ev.savings.fuelConsumption.unit",
            message: "{usesMiles, select, true {l/mil} other {l/100 km}}",
            comment:
              "Unit after the fuel consumption on the savings calculator card on electric car ads. Litres per Scandinavian mil where distance is quoted that way (FINN, Blocket), litres per 100 km elsewhere (DBA).",
            values: { usesMiles },
          })}
          value={fuelConsumption}
          {...consumptionBounds}
          onChange={(next) => setFuelConsumptions((consumptions) => ({ ...consumptions, [fuelType]: next }))}
        />
      </div>
    </>
  );
};

type CalculatorSliderProps = {
  name: string;
  label: string;
  /** Shown through our own icon: `w-slider`'s `tooltip` renders outside the label markup we slot in. */
  tooltip?: string;
  /** Translated already, e.g. "mil" or "kr/kWh". */
  unit: string;
  value: number;
  min: number;
  max: number;
  step: number;
  decimals?: number;
  onChange: (value: number) => void;
};

/** One input of the calculator. Warp's own text field is hidden, so the value only shows where we put it. */
const CalculatorSlider = ({
  name,
  label,
  tooltip,
  unit,
  value,
  min,
  max,
  step,
  decimals = 0,
  onChange,
}: CalculatorSliderProps) => {
  const { locale } = useAppConfig();

  const format = useCallback(
    (amount: number) =>
      formatNumber(amount, locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) ??
      String(amount),
    [locale, decimals],
  );

  // Every locale we ship writes number then unit; the non-breaking space holds them on one line.
  const formatWithUnit = useCallback((amount: number) => `${format(amount)}\u00a0${unit}`, [format, unit]);

  const sliderRef = useRef<ComponentRef<typeof Slider>>(null);
  const thumbRef = useRef<ComponentRef<typeof SliderThumb>>(null);

  // JS-only properties: React drops function props on a custom element it has not seen upgrade.
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) {
      return;
    }

    // The element appends the suffix itself in the drag tooltip, so this stays a bare number.
    slider.valueFormatter = (amount) => format(Number(amount));
    // An empty formatter is the only way to drop the min and max labels; there is no attribute.
    slider.labelFormatter = () => "";
  }, [format]);

  // The filled part of the track is redrawn from the thumb's own events, so a value set from
  // outside — switching fuel type — moves the handle but leaves the fill behind. This is the
  // event the element uses for exactly that.
  useEffect(() => {
    const thumb = thumbRef.current;
    if (!thumb) {
      return;
    }

    thumb.value = String(value);
    thumb.dispatchEvent(new CustomEvent("thumbreset", { bubbles: true }));
  }, [value]);

  return (
    <div>
      {/* Outside `w-slider`, not in its `label` slot: inside the shadow root the tooltip panel is
       * positioned against the document and lands far down the page. Wraps so a label too long to
       * share the line drops the value beneath it.
       */}
      <div className="mb-8 flex flex-wrap items-baseline justify-between gap-x-8 gap-y-4">
        {/* Only where there is an icon to strand — the sliders without one have the longest labels. */}
        <span className={tooltip ? "whitespace-nowrap" : undefined}>
          <span className="t5">{label}</span>
          {/* Boxed to the icon's own 16px: left to itself it makes a taller line than the text, and
           * the sliders that carry one would sit further from their label than the sliders that do not.
           */}
          {tooltip && (
            <span className="inline-flex h-16 items-center align-middle">
              <Tooltip tooltip={tooltip} label={label} />
            </span>
          )}
        </span>
        {/* Hidden from assistive technology: the range input announces the value itself. */}
        <span className="text-m" aria-hidden="true">
          {formatWithUnit(value)}
        </span>
      </div>

      <Slider ref={sliderRef} min={String(min)} max={String(max)} step={step} suffix={unit} hiddenTextfield>
        <SliderThumb
          ref={thumbRef}
          name={name}
          // Without a `label` attribute the slider has no text to hand the thumb.
          aria-label={label}
          value={String(value)}
          onInput={(event) => {
            const next = Number(event.currentTarget.value);
            // Against the slider's own range, not zero: none of them start at zero.
            if (Number.isFinite(next) && next >= min && next <= max) {
              onChange(next);
            }
          }}
        />
      </Slider>
    </div>
  );
};
